import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Typography, SkeletonWrapper } from "@/components/atoms";
import { useGetOnboardingStatusQuery } from "@/redux/apis/onboarding/onboardingApi";
import { setHasSeenOnboardingCompleteModal, selectHasSeenOnboardingCompleteModal } from "@/redux/slices/quickSetup";
import { QuickSetupSkeleton } from "./components";
import { isOnboardingFullyComplete } from "./helpers/onboardingHelpers";
import { useAutoScrollToPanel } from "./hooks";
import { OnboardingCompleteModal } from "./modals";
import {
  GettingStartedPanel,
  BusinessSettingsPanel,
  TripSettingsPanel,
  RunTestSimulationPanel,
  FleetSetupPanel,
  ClientCompanyOnboardingPanel,
  AddonsPanel,
} from "./panels";

export const QuickSetupPanel = () => {
  useAutoScrollToPanel();
  const dispatch = useDispatch();
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  const { data: onboardingStatus, isLoading } = useGetOnboardingStatusQuery(undefined, {
    pollingInterval: 60000,
    refetchOnFocus: true,
  });
  const hasSeenCompletionModal = useSelector(selectHasSeenOnboardingCompleteModal);

  const isOnboardingComplete = isOnboardingFullyComplete(onboardingStatus);
  useEffect(() => {
    if (isOnboardingComplete && !hasSeenCompletionModal) {
      setShowCompletionModal(true);
    }
  }, [onboardingStatus, hasSeenCompletionModal, isOnboardingComplete]);

  const handleCloseCompletionModal = () => {
    dispatch(setHasSeenOnboardingCompleteModal(true));
    setShowCompletionModal(false);
  };

  return (
    <SkeletonWrapper isLoading={isLoading} skeleton={<QuickSetupSkeleton />}>
      <div className="flex justify-center bg-neutral-surface-gray py-4 ">
        <div className="w-full max-w-[705px]">
          <div className="mb-6 text-center">
            <Typography variant="h2" className="mb-1 text-neutral-dark-gray">
              {isOnboardingComplete ? "Your Setup Guide" : "Getting Started"}🎉
            </Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              {isOnboardingComplete
                ? "Revisit key settings as an ongoing resource for your business"
                : "Just a few quick steps to set up your account so you can start booking and managing limo rides with ease."}
            </Typography>
          </div>

          <div className="flex flex-col gap-5">
            <GettingStartedPanel defaultOpen />
            <BusinessSettingsPanel />
            <TripSettingsPanel />
            <RunTestSimulationPanel />
            <FleetSetupPanel />
            <ClientCompanyOnboardingPanel />
            <AddonsPanel />
          </div>
        </div>

        <OnboardingCompleteModal open={showCompletionModal} onClose={handleCloseCompletionModal} />
      </div>
    </SkeletonWrapper>
  );
};
